/**
 * Serviço de Coleta de Dados Comportamentais
 * Responsável por capturar e analisar padrões de comportamento do usuário
 */

export class BehavioralService {
    constructor(config, logger) {
        this.config = config;
        this.logger = logger.child('BehavioralService');
        this.isCollecting = false;
        this.eventListeners = new Map();
        this.behavioralData = {
            mouse: {
                movements: [],
                clicks: [],
                scrolls: [],
                hovers: []
            },
            keyboard: {
                keystrokes: [],
                patterns: [],
                timing: []
            },
            touch: {
                touches: [],
                gestures: [],
                pressure: []
            },
            focus: {
                events: [],
                duration: [],
                switches: 0
            },
            viewport: {
                changes: [],
                orientation: [],
                visibility: []
            }
        };
        this.stats = {
            eventsCollected: 0,
            sessionStart: null,
            lastActivity: null,
            totalEvents: {
                mouse: 0,
                keyboard: 0,
                touch: 0,
                focus: 0,
                viewport: 0
            }
        };
        this.analysisCache = new Map();
    }

    /**
     * Inicia coleta de dados comportamentais
     */
    startCollection() {
        if (!this.config.behavioralEnabled || this.isCollecting) {
            this.logger.debug('Coleta comportamental já ativa ou desabilitada');
            return;
        }

        this.isCollecting = true;
        this.stats.sessionStart = Date.now();
        this.stats.lastActivity = Date.now();

        this.setupEventListeners();

        this.logger.info('Coleta comportamental iniciada');
    }

    /**
     * Para coleta de dados comportamentais
     */
    stopCollection() {
        if (!this.isCollecting) {
            return;
        }

        this.isCollecting = false;
        this.removeEventListeners();

        this.logger.info('Coleta comportamental parada', {
            duration: Date.now() - this.stats.sessionStart,
            eventsCollected: this.stats.eventsCollected
        });
    }

    /**
     * Configura listeners de eventos
     */
    setupEventListeners() {
        // Eventos de mouse
        this.addEventListeners([
            { event: 'mousemove', handler: this.handleMouseMove.bind(this) },
            { event: 'mousedown', handler: this.handleMouseDown.bind(this) },
            { event: 'mouseup', handler: this.handleMouseUp.bind(this) },
            { event: 'click', handler: this.handleClick.bind(this) },
            { event: 'wheel', handler: this.handleScroll.bind(this) },
            { event: 'mouseenter', handler: this.handleMouseEnter.bind(this) },
            { event: 'mouseleave', handler: this.handleMouseLeave.bind(this) }
        ]);

        // Eventos de teclado
        this.addEventListeners([
            { event: 'keydown', handler: this.handleKeyDown.bind(this) },
            { event: 'keyup', handler: this.handleKeyUp.bind(this) },
            { event: 'keypress', handler: this.handleKeyPress.bind(this) }
        ]);

        // Eventos de toque
        this.addEventListeners([
            { event: 'touchstart', handler: this.handleTouchStart.bind(this) },
            { event: 'touchmove', handler: this.handleTouchMove.bind(this) },
            { event: 'touchend', handler: this.handleTouchEnd.bind(this) },
            { event: 'touchcancel', handler: this.handleTouchCancel.bind(this) }
        ]);

        // Eventos de foco e visibilidade
        this.addEventListeners([
            { event: 'focus', handler: this.handleFocus.bind(this) },
            { event: 'blur', handler: this.handleBlur.bind(this) },
            { event: 'visibilitychange', handler: this.handleVisibilityChange.bind(this) }
        ]);

        // Eventos de viewport
        this.addEventListeners([
            { event: 'resize', handler: this.handleResize.bind(this) },
            { event: 'orientationchange', handler: this.handleOrientationChange.bind(this) },
            { event: 'scroll', handler: this.handleWindowScroll.bind(this) }
        ]);

        // Eventos personalizados
        this.addEventListeners([
            { event: 'beforeunload', handler: this.handleBeforeUnload.bind(this) },
            { event: 'pagehide', handler: this.handlePageHide.bind(this) }
        ]);
    }

    /**
     * Adiciona listeners de eventos
     */
    addEventListeners(listeners) {
        listeners.forEach(({ event, handler }) => {
            try {
                const options = {
                    passive: true,
                    capture: false
                };

                document.addEventListener(event, handler, options);
                window.addEventListener(event, handler, options);

                this.eventListeners.set(event, handler);

            } catch (error) {
                this.logger.debug(`Erro ao adicionar listener para ${event}`, { error: error.message });
            }
        });
    }

    /**
     * Remove listeners de eventos
     */
    removeEventListeners() {
        this.eventListeners.forEach((handler, event) => {
            try {
                document.removeEventListener(event, handler);
                window.removeEventListener(event, handler);
            } catch (error) {
                this.logger.debug(`Erro ao remover listener para ${event}`, { error: error.message });
            }
        });

        this.eventListeners.clear();
    }

    /**
     * Manipula movimento do mouse
     */
    handleMouseMove(event) {
        if (!this.shouldCollectEvent('mouse')) return;

        const timestamp = Date.now();
        const movement = {
            x: event.clientX,
            y: event.clientY,
            timestamp,
            target: this.getElementInfo(event.target)
        };

        this.behavioralData.mouse.movements.push(movement);
        this.updateStats('mouse');

        // Mantém apenas os últimos N movimentos
        if (this.behavioralData.mouse.movements.length > this.config.maxBehavioralEvents) {
            this.behavioralData.mouse.movements.shift();
        }
    }

    /**
     * Manipula cliques do mouse
     */
    handleClick(event) {
        if (!this.shouldCollectEvent('mouse')) return;

        const timestamp = Date.now();
        const click = {
            x: event.clientX,
            y: event.clientY,
            button: event.button,
            timestamp,
            target: this.getElementInfo(event.target),
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey
        };

        this.behavioralData.mouse.clicks.push(click);
        this.updateStats('mouse');
    }

    /**
     * Manipula eventos de mouse down/up
     */
    handleMouseDown(event) {
        this.mouseDownTime = Date.now();
        this.mouseDownPosition = { x: event.clientX, y: event.clientY };
    }

    handleMouseUp(event) {
        if (this.mouseDownTime && this.mouseDownPosition) {
            const duration = Date.now() - this.mouseDownTime;
            const distance = Math.sqrt(
                Math.pow(event.clientX - this.mouseDownPosition.x, 2) +
                Math.pow(event.clientY - this.mouseDownPosition.y, 2)
            );

            // Adiciona dados de pressão/arrasto
            this.behavioralData.mouse.clicks.push({
                type: 'press_release',
                duration,
                distance,
                timestamp: Date.now(),
                startPos: this.mouseDownPosition,
                endPos: { x: event.clientX, y: event.clientY }
            });
        }
    }

    /**
     * Manipula scroll
     */
    handleScroll(event) {
        if (!this.shouldCollectEvent('mouse')) return;

        const scroll = {
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaZ: event.deltaZ,
            deltaMode: event.deltaMode,
            timestamp: Date.now(),
            target: this.getElementInfo(event.target)
        };

        this.behavioralData.mouse.scrolls.push(scroll);
        this.updateStats('mouse');
    }

    /**
     * Manipula hover
     */
    handleMouseEnter(event) {
        this.hoverStartTime = Date.now();
        this.hoverTarget = event.target;
    }

    handleMouseLeave(event) {
        if (this.hoverStartTime && this.hoverTarget === event.target) {
            const hover = {
                duration: Date.now() - this.hoverStartTime,
                target: this.getElementInfo(event.target),
                timestamp: Date.now()
            };

            this.behavioralData.mouse.hovers.push(hover);
            this.updateStats('mouse');
        }
    }

    /**
     * Manipula eventos de teclado
     */
    handleKeyDown(event) {
        if (!this.shouldCollectEvent('keyboard')) return;

        this.keyDownTime = Date.now();
        this.lastKeyCode = event.code;
    }

    handleKeyUp(event) {
        if (!this.shouldCollectEvent('keyboard')) return;

        const timestamp = Date.now();
        const duration = this.keyDownTime ? timestamp - this.keyDownTime : 0;

        const keystroke = {
            code: event.code,
            key: event.key.length === 1 ? 'char' : event.key, // Não armazena caracteres específicos
            duration,
            timestamp,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            metaKey: event.metaKey
        };

        this.behavioralData.keyboard.keystrokes.push(keystroke);
        this.updateStats('keyboard');

        // Analisa padrões de digitação
        this.analyzeTypingPattern(keystroke);
    }

    handleKeyPress(event) {
        // Coleta timing entre teclas
        if (this.lastKeyTime) {
            const interval = Date.now() - this.lastKeyTime;
            this.behavioralData.keyboard.timing.push(interval);
        }
        this.lastKeyTime = Date.now();
    }

    /**
     * Manipula eventos de toque
     */
    handleTouchStart(event) {
        if (!this.shouldCollectEvent('touch')) return;

        Array.from(event.touches).forEach(touch => {
            const touchData = {
                identifier: touch.identifier,
                x: touch.clientX,
                y: touch.clientY,
                force: touch.force || 0,
                radiusX: touch.radiusX || 0,
                radiusY: touch.radiusY || 0,
                rotationAngle: touch.rotationAngle || 0,
                timestamp: Date.now(),
                type: 'start'
            };

            this.behavioralData.touch.touches.push(touchData);
            this.updateStats('touch');
        });
    }

    handleTouchMove(event) {
        if (!this.shouldCollectEvent('touch')) return;

        Array.from(event.touches).forEach(touch => {
            const touchData = {
                identifier: touch.identifier,
                x: touch.clientX,
                y: touch.clientY,
                force: touch.force || 0,
                timestamp: Date.now(),
                type: 'move'
            };

            this.behavioralData.touch.touches.push(touchData);
            this.updateStats('touch');
        });
    }

    handleTouchEnd(event) {
        if (!this.shouldCollectEvent('touch')) return;

        Array.from(event.changedTouches).forEach(touch => {
            const touchData = {
                identifier: touch.identifier,
                x: touch.clientX,
                y: touch.clientY,
                timestamp: Date.now(),
                type: 'end'
            };

            this.behavioralData.touch.touches.push(touchData);
            this.updateStats('touch');
        });

        // Analisa gestos
        this.analyzeGestures();
    }

    handleTouchCancel(event) {
        // Similar ao touchEnd mas marca como cancelado
        this.handleTouchEnd(event);
    }

    /**
     * Manipula eventos de foco
     */
    handleFocus(event) {
        const focusEvent = {
            type: 'focus',
            target: this.getElementInfo(event.target),
            timestamp: Date.now()
        };

        this.behavioralData.focus.events.push(focusEvent);
        this.focusStartTime = Date.now();
        this.updateStats('focus');
    }

    handleBlur(event) {
        const blurEvent = {
            type: 'blur',
            target: this.getElementInfo(event.target),
            timestamp: Date.now()
        };

        this.behavioralData.focus.events.push(blurEvent);

        if (this.focusStartTime) {
            const duration = Date.now() - this.focusStartTime;
            this.behavioralData.focus.duration.push(duration);
        }

        this.behavioralData.focus.switches++;
        this.updateStats('focus');
    }

    /**
     * Manipula mudanças de visibilidade
     */
    handleVisibilityChange() {
        const visibilityEvent = {
            hidden: document.hidden,
            visibilityState: document.visibilityState,
            timestamp: Date.now()
        };

        this.behavioralData.viewport.visibility.push(visibilityEvent);
        this.updateStats('viewport');
    }

    /**
     * Manipula redimensionamento
     */
    handleResize() {
        const resizeEvent = {
            width: window.innerWidth,
            height: window.innerHeight,
            timestamp: Date.now()
        };

        this.behavioralData.viewport.changes.push(resizeEvent);
        this.updateStats('viewport');
    }

    /**
     * Manipula mudança de orientação
     */
    handleOrientationChange() {
        const orientationEvent = {
            orientation: screen.orientation ? screen.orientation.angle : window.orientation,
            timestamp: Date.now()
        };

        this.behavioralData.viewport.orientation.push(orientationEvent);
        this.updateStats('viewport');
    }

    /**
     * Manipula scroll da janela
     */
    handleWindowScroll() {
        if (!this.shouldCollectEvent('viewport')) return;

        const scrollEvent = {
            scrollX: window.scrollX,
            scrollY: window.scrollY,
            timestamp: Date.now()
        };

        this.behavioralData.viewport.changes.push(scrollEvent);
        this.updateStats('viewport');
    }

    /**
     * Manipula eventos de saída
     */
    handleBeforeUnload() {
        this.logger.debug('Página sendo descarregada, finalizando coleta');
        this.stopCollection();
    }

    handlePageHide() {
        this.logger.debug('Página oculta, pausando coleta');
    }

    /**
     * Verifica se deve coletar evento
     */
    shouldCollectEvent(type) {
        if (!this.isCollecting) return false;

        // Throttling baseado em configuração
        const now = Date.now();
        const lastCollection = this.lastCollectionTime?.[type] || 0;
        const minInterval = this.config.collectionInterval?.[type] || 10;

        if (now - lastCollection < minInterval) {
            return false;
        }

        if (!this.lastCollectionTime) {
            this.lastCollectionTime = {};
        }
        this.lastCollectionTime[type] = now;

        return true;
    }

    /**
     * Obtém informações do elemento
     */
    getElementInfo(element) {
        if (!element) return null;

        return {
            tagName: element.tagName?.toLowerCase(),
            id: element.id || null,
            className: element.className || null,
            type: element.type || null
        };
    }

    /**
     * Analisa padrões de digitação
     */
    analyzeTypingPattern(keystroke) {
        const recentKeystrokes = this.behavioralData.keyboard.keystrokes.slice(-10);

        if (recentKeystrokes.length >= 2) {
            const intervals = [];
            for (let i = 1; i < recentKeystrokes.length; i++) {
                intervals.push(recentKeystrokes[i].timestamp - recentKeystrokes[i - 1].timestamp);
            }

            const pattern = {
                avgInterval: intervals.reduce((a, b) => a + b, 0) / intervals.length,
                minInterval: Math.min(...intervals),
                maxInterval: Math.max(...intervals),
                variance: this.calculateVariance(intervals),
                timestamp: Date.now()
            };

            this.behavioralData.keyboard.patterns.push(pattern);
        }
    }

    /**
     * Analisa gestos de toque
     */
    analyzeGestures() {
        const recentTouches = this.behavioralData.touch.touches.slice(-20);
        const touchSessions = this.groupTouchesBySession(recentTouches);

        touchSessions.forEach(session => {
            if (session.length >= 3) {
                const gesture = this.identifyGesture(session);
                if (gesture) {
                    this.behavioralData.touch.gestures.push(gesture);
                }
            }
        });
    }

    /**
     * Agrupa toques por sessão
     */
    groupTouchesBySession(touches) {
        const sessions = new Map();

        touches.forEach(touch => {
            if (!sessions.has(touch.identifier)) {
                sessions.set(touch.identifier, []);
            }
            sessions.get(touch.identifier).push(touch);
        });

        return Array.from(sessions.values());
    }

    /**
     * Identifica tipo de gesto
     */
    identifyGesture(touchSession) {
        if (touchSession.length < 3) return null;

        const startTouch = touchSession[0];
        const endTouch = touchSession[touchSession.length - 1];

        const distance = Math.sqrt(
            Math.pow(endTouch.x - startTouch.x, 2) +
            Math.pow(endTouch.y - startTouch.y, 2)
        );

        const duration = endTouch.timestamp - startTouch.timestamp;
        const velocity = distance / duration;

        let gestureType = 'tap';
        if (distance > 50) {
            gestureType = velocity > 0.5 ? 'swipe' : 'drag';
        }

        return {
            type: gestureType,
            distance,
            duration,
            velocity,
            startPos: { x: startTouch.x, y: startTouch.y },
            endPos: { x: endTouch.x, y: endTouch.y },
            timestamp: endTouch.timestamp
        };
    }

    /**
     * Calcula variância
     */
    calculateVariance(values) {
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const squaredDiffs = values.map(value => Math.pow(value - mean, 2));
        return squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
    }

    /**
     * Atualiza estatísticas
     */
    updateStats(type) {
        this.stats.eventsCollected++;
        this.stats.totalEvents[type]++;
        this.stats.lastActivity = Date.now();
    }

    /**
     * Obtém dados comportamentais processados
     */
    getBehavioralData() {
        const cacheKey = `behavioral_${Date.now()}`;

        if (this.analysisCache.has(cacheKey)) {
            return this.analysisCache.get(cacheKey);
        }

        const processedData = {
            // Estatísticas gerais
            session: {
                duration: this.stats.sessionStart ? Date.now() - this.stats.sessionStart : 0,
                eventsCollected: this.stats.eventsCollected,
                lastActivity: this.stats.lastActivity,
                eventsByType: { ...this.stats.totalEvents }
            },

            // Análise de mouse
            mouse: this.analyzeMouseBehavior(),

            // Análise de teclado
            keyboard: this.analyzeKeyboardBehavior(),

            // Análise de toque
            touch: this.analyzeTouchBehavior(),

            // Análise de foco
            focus: this.analyzeFocusBehavior(),

            // Análise de viewport
            viewport: this.analyzeViewportBehavior(),

            // Pontuação de risco
            riskScore: this.calculateRiskScore(),

            // Raw events (added for payload requirements)
            events: this.behavioralData
        };

        this.analysisCache.set(cacheKey, processedData);

        // Limpa cache antigo
        setTimeout(() => this.analysisCache.delete(cacheKey), 60000);

        return processedData;
    }

    /**
     * Analisa comportamento do mouse
     */
    analyzeMouseBehavior() {
        const movements = this.behavioralData.mouse.movements;
        const clicks = this.behavioralData.mouse.clicks;

        if (movements.length === 0) {
            return { hasData: false };
        }

        // Calcula velocidades
        const velocities = [];
        for (let i = 1; i < movements.length; i++) {
            const prev = movements[i - 1];
            const curr = movements[i];
            const distance = Math.sqrt(Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2));
            const time = curr.timestamp - prev.timestamp;
            velocities.push(time > 0 ? distance / time : 0);
        }

        return {
            hasData: true,
            movementCount: movements.length,
            clickCount: clicks.length,
            avgVelocity: velocities.length > 0 ? velocities.reduce((a, b) => a + b, 0) / velocities.length : 0,
            maxVelocity: velocities.length > 0 ? Math.max(...velocities) : 0,
            velocityVariance: velocities.length > 0 ? this.calculateVariance(velocities) : 0,
            clickFrequency: clicks.length > 0 ? this.stats.sessionStart ? clicks.length / ((Date.now() - this.stats.sessionStart) / 1000) : 0 : 0
        };
    }

    /**
     * Analisa comportamento do teclado
     */
    analyzeKeyboardBehavior() {
        const keystrokes = this.behavioralData.keyboard.keystrokes;
        const patterns = this.behavioralData.keyboard.patterns;

        if (keystrokes.length === 0) {
            return { hasData: false };
        }

        const durations = keystrokes.map(k => k.duration).filter(d => d > 0);

        return {
            hasData: true,
            keystrokeCount: keystrokes.length,
            avgKeyDuration: durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0,
            typingRhythm: patterns.length > 0 ? patterns[patterns.length - 1].avgInterval : 0,
            patternCount: patterns.length
        };
    }

    /**
     * Analisa comportamento de toque
     */
    analyzeTouchBehavior() {
        const touches = this.behavioralData.touch.touches;
        const gestures = this.behavioralData.touch.gestures;

        if (touches.length === 0) {
            return { hasData: false };
        }

        const pressures = touches.map(t => t.force).filter(f => f > 0);

        return {
            hasData: true,
            touchCount: touches.length,
            gestureCount: gestures.length,
            avgPressure: pressures.length > 0 ? pressures.reduce((a, b) => a + b, 0) / pressures.length : 0,
            maxPressure: pressures.length > 0 ? Math.max(...pressures) : 0
        };
    }

    /**
     * Analisa comportamento de foco
     */
    analyzeFocusBehavior() {
        const events = this.behavioralData.focus.events;
        const durations = this.behavioralData.focus.duration;

        return {
            hasData: events.length > 0,
            focusEvents: events.length,
            focusSwitches: this.behavioralData.focus.switches,
            avgFocusDuration: durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0
        };
    }

    /**
     * Analisa comportamento do viewport
     */
    analyzeViewportBehavior() {
        const changes = this.behavioralData.viewport.changes;
        const visibility = this.behavioralData.viewport.visibility;

        return {
            hasData: changes.length > 0 || visibility.length > 0,
            viewportChanges: changes.length,
            visibilityChanges: visibility.length,
            orientationChanges: this.behavioralData.viewport.orientation.length
        };
    }

    /**
     * Calcula pontuação de risco
     */
    calculateRiskScore() {
        let score = 0;
        const factors = [];

        // Verifica padrões suspeitos
        const mouseAnalysis = this.analyzeMouseBehavior();
        if (mouseAnalysis.hasData) {
            // Velocidade muito alta ou muito baixa pode ser suspeita
            if (mouseAnalysis.maxVelocity > 10 || mouseAnalysis.avgVelocity < 0.1) {
                score += 20;
                factors.push('Velocidade de mouse suspeita');
            }

            // Variância muito baixa pode indicar automação
            if (mouseAnalysis.velocityVariance < 0.01) {
                score += 30;
                factors.push('Padrão de movimento muito regular');
            }
        }

        const keyboardAnalysis = this.analyzeKeyboardBehavior();
        if (keyboardAnalysis.hasData) {
            // Ritmo de digitação muito regular
            if (keyboardAnalysis.typingRhythm > 0 && keyboardAnalysis.typingRhythm < 50) {
                score += 25;
                factors.push('Ritmo de digitação muito regular');
            }
        }

        // Falta de atividade comportamental
        if (this.stats.eventsCollected < 10) {
            score += 15;
            factors.push('Pouca atividade comportamental');
        }

        return {
            score: Math.min(score, 100),
            level: score < 30 ? 'low' : score < 60 ? 'medium' : 'high',
            factors
        };
    }

    /**
     * Limpa dados comportamentais
     */
    clearBehavioralData() {
        this.behavioralData = {
            mouse: { movements: [], clicks: [], scrolls: [], hovers: [] },
            keyboard: { keystrokes: [], patterns: [], timing: [] },
            touch: { touches: [], gestures: [], pressure: [] },
            focus: { events: [], duration: [], switches: 0 },
            viewport: { changes: [], orientation: [], visibility: [] }
        };

        this.stats.eventsCollected = 0;
        this.stats.totalEvents = { mouse: 0, keyboard: 0, touch: 0, focus: 0, viewport: 0 };
        this.analysisCache.clear();

        this.logger.debug('Dados comportamentais limpos');
    }

    /**
     * Obtém estatísticas do serviço
     */
    getStats() {
        return {
            isCollecting: this.isCollecting,
            sessionDuration: this.stats.sessionStart ? Date.now() - this.stats.sessionStart : 0,
            eventsCollected: this.stats.eventsCollected,
            eventsByType: { ...this.stats.totalEvents },
            lastActivity: this.stats.lastActivity,
            cacheSize: this.analysisCache.size
        };
    }

    /**
     * Atualiza configuração
     */
    updateConfig(newConfig) {
        const wasCollecting = this.isCollecting;

        if (wasCollecting) {
            this.stopCollection();
        }

        this.config = newConfig;

        if (wasCollecting && newConfig.behavioralEnabled) {
            this.startCollection();
        }

        this.logger.debug('Configuração do BehavioralService atualizada');
    }

    /**
     * Testa suporte a eventos comportamentais
     */
    static isSupported() {
        return !!(document.addEventListener && window.addEventListener);
    }

    /**
     * Obtém capacidades de coleta comportamental
     */
    static getCapabilities() {
        return {
            supported: BehavioralService.isSupported(),
            touchSupported: 'ontouchstart' in window,
            pointerSupported: 'onpointerdown' in window,
            orientationSupported: 'onorientationchange' in window,
            visibilitySupported: 'visibilityState' in document,
            focusSupported: 'onfocus' in window
        };
    }

    /**
     * Limpa recursos
     */
    destroy() {
        this.stopCollection();
        this.clearBehavioralData();
        this.logger.debug('BehavioralService destruído');
    }
}